import { useState } from 'react';
import axios from 'axios';
import CONFIG from '@/config';
import {
  getRolesForPayload,
  parseToken,
  setAuthLogin
} from '@/common/utils/AuthUser';
import { isNonPowUser } from '@/models/common';
import { getRestHeaders } from '@/common/utils/api';
import { storeId2Gql } from '@/common/utils/apiConversionUtils';
import { setFeatureFlags } from '@/common/utils/featureFlags';
import { useAnalytics } from '@restore-hw/analytics';

export const ERROR = {
  UNKNOWN: 'Internal server error',
  INCORRECT_PERMISSIONS: 'You do not have permission to login to this service.',
  INVALID_CREDENTIALS: 'Invalid Credentials',
  TRY_AGAIN_LATER:
    'There was a problem attempting to login, please try again later',
  TOKEN_EXPIRED: 'Temporary login has expired, please log in again'
} as const;

type Values = {
  username: string;
  password: string;
};

export function setLogin(data: any) {
  let login = {};
  if (data.type === 'TOKEN') {
    // Full login
    const payload = parseToken(data.token);
    if (!payload) {
      throw new Error(ERROR.TRY_AGAIN_LATER);
    }

    const roles = getRolesForPayload(payload);
    if (isNonPowUser(roles)) {
      throw new Error(ERROR.INCORRECT_PERMISSIONS);
    }

    const { tz, ...store } = data.store;
    login = {
      token: data.token,
      user: {
        ...data.user,
        terminalLocation: data.terminalLocation || '',
        store: {
          ...store,
          id: storeId2Gql(store.id),
          hours: JSON.stringify(store.hours),
          timezoneIdentifier: { identifier: tz }
        }
      }
    };
  } else {
    // Temporary login
    login = {
      token: data.token
    };
  }
  setAuthLogin(login);
}

export function useLogin() {
  const [loading, setLoading] = useState(false);
  const { analytics } = useAnalytics();

  return {
    mutate: async (values: Values) => {
      setLoading(true);

      return axios
        .post(
          `${CONFIG.USERS_API_URL}/auth/v2/staff/token`,
          {
            username: values.username,
            password: values.password
          },
          {
            withCredentials: true
          }
        )
        .then(res => {
          const user = res.data.data.user;

          setLogin(res.data.data);

          analytics?.identify(user.id.toString(), {
            first_name: user.firstName,
            last_name: user.lastName
          });
          return res.data;
        })
        .catch(error => {
          const errorResponse = error?.response?.data;
          throw errorResponse;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    isLoading: loading
  };
}

export function useSetStore() {
  const [loading, setLoading] = useState(false);

  return {
    mutate: async (store: number) => {
      setLoading(true);

      return axios
        .post(
          `${CONFIG.USERS_API_URL}/auth/v2/staff/select-store`,
          { store: store },
          { headers: getRestHeaders(), withCredentials: true }
        )
        .then(res => {
          setLogin(res.data.data);
          return res.data;
        })
        .catch(error => {
          if (error.response.status === 401) {
            throw ERROR.TOKEN_EXPIRED;
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    isLoading: loading
  };
}

export function useGetFeatureFlags() {
  const [loading, setLoading] = useState(false);

  return {
    get: async () => {
      setLoading(true);

      return axios
        .get(`${CONFIG.USERS_API_URL}/staff/feature-flags`, {
          headers: getRestHeaders()
        })
        .then(res => {
          setFeatureFlags(res?.data?.data?.flags);
        })
        .catch(error => {
          if (error?.response?.status === 401) {
            throw ERROR.TOKEN_EXPIRED;
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    isLoading: loading
  };
}
